import './styles/main.css';
import { COLLECTORS } from './collectors';
import { buildTips, detectBrowser } from './content/tips';
import { ESTIMATE_METHOD_VERSION } from './entropy/estimates';
import { runCheck, type Phase } from './run';
import { byId } from './ui/dom';
import { APP_VERSION } from './ui/export';
import { initCompare } from './ui/compare-view';
import { renderAbout, renderProgressList, renderResults, updateProgress } from './ui/render';

// ── Theme: auto (system) → light → dark. Kept in memory only; this page writes nothing to storage. ──
type Theme = 'auto' | 'light' | 'dark';
const THEMES: Theme[] = ['auto', 'light', 'dark'];
const THEME_LABEL: Record<Theme, string> = { auto: 'Theme: auto', light: 'Theme: light', dark: 'Theme: dark' };

function initTheme() {
  const button = byId<HTMLButtonElement>('theme-toggle');
  let current: Theme = 'auto';
  const apply = () => {
    if (current === 'auto') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = current;
    button.textContent = THEME_LABEL[current];
  };
  button.addEventListener('click', () => {
    current = THEMES[(THEMES.indexOf(current) + 1) % THEMES.length]!;
    apply();
  });
  apply();
}

// ── About-the-score dialog ──
function initAbout(): () => void {
  const dialog = byId<HTMLDialogElement>('about-score');
  renderAbout(byId('about-body'));
  byId('about-close').addEventListener('click', () => dialog.close());
  // Close when clicking the backdrop.
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });
  const open = () => dialog.showModal();
  for (const el of document.querySelectorAll<HTMLElement>('[data-open-about]')) el.addEventListener('click', open);
  return open;
}

const PHASE_TEXT: Record<Phase, string> = {
  sampling: 'Collecting signals…',
  resampling: 'Sampling everything a second time to detect randomization…',
  scoring: 'Scoring…',
};

function init() {
  initTheme();
  const openAbout = initAbout();
  byId('app-version').textContent = APP_VERSION;
  byId('method-version').textContent = ESTIMATE_METHOD_VERSION;
  byId('category-count').textContent = String(COLLECTORS.length);
  const compare = initCompare(byId('compare-app'));

  const runButton = byId<HTMLButtonElement>('run-btn');
  const status = byId('run-status');
  const progress = byId('progress');
  let items = renderProgressList(progress, COLLECTORS);

  const hiddenUntilRun = ['summary', 'details', 'reduce', 'export'].map((id) => byId(id));

  runButton.addEventListener('click', async () => {
    runButton.disabled = true;
    runButton.textContent = 'Checking…';
    items = renderProgressList(progress, COLLECTORS);
    let done = 0;
    try {
      const result = await runCheck({
        onPhase: (phase) => {
          status.textContent = PHASE_TEXT[phase];
        },
        onProgress: (id, state, ms) => {
          updateProgress(items.get(id), state, ms);
          if (state !== 'running') status.textContent = `Collected ${++done} of ${COLLECTORS.length} categories…`;
        },
      });

      const byIdMap = new Map(result.signals.map((s) => [s.id, s]));
      const browser = detectBrowser(byIdMap, result.uniformMode);
      const tips = buildTips(byIdMap, browser, result.protections);
      const report = renderResults(
        result,
        browser,
        tips,
        {
          summary: byId('summary-content'),
          categories: byId('categories'),
          tips: byId('tips'),
          tipsIntro: byId('tips-intro'),
          exportActions: byId('export-actions'),
          share: byId('share'),
          whatIfBar: byId('whatif-bar'),
        },
        openAbout,
      );
      compare.setCurrent(report);
      for (const el of hiddenUntilRun) el.hidden = false;
      status.textContent = `Done: ${result.signals.length} signals from ${result.categories.length} categories. Nothing was sent anywhere.`;
      // Move focus to the result so keyboard and screen-reader users land on it.
      const heading = document.getElementById('summary-title');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      byId('summary').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    } catch (err) {
      status.textContent = `Something went wrong: ${err instanceof Error ? err.message : String(err)}`;
    } finally {
      runButton.disabled = false;
      runButton.textContent = 'Run the check again';
    }
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
